'use client';

import { getNestedConditions } from '@/components/Form/helper/conditionsHelper';
import type { FormContentItem, FormValues, Validator } from '@/components/Form/types';
import type { FormStoryblok, RegexStoryblok } from '@/types/component-types-sb';
import { isFormMarketingOptIn } from '@/types/typeguards';
import * as Yup from 'yup';

import {
  createOptionalDateYupSchema,
  createOptionalNumberYupSchema,
  isSkippableEmptyFormValue,
} from './optionalEmptyYup';
import {
  createDateConstraintSchema,
  createEmailValidator,
  createRegexMatchSchema,
  createStringLengthSchema,
} from './schemaBuilders';
import type { Services } from './schemaHelper';
import {
  calculateDateFromNow,
  replaceDateInErrorMessage,
  replaceLengthInErrorMessage,
  shouldApplyValidation,
} from './validationUtils';

/**
 * Applies numeric max length constraint to a field schema
 *
 * @param fieldSchema - The current field schema
 * @param maxLength - The maximum length
 * @param numericErrorMessage - The error message for numeric validation
 * @param maxLengthErrorMessage - The error message for max length validation
 * @returns The updated field schema
 */
export const applyNumericMaxLength = (
  fieldSchema: Yup.Schema<unknown>,
  maxLength: boolean | number,
  numericErrorMessage: string,
  maxLengthErrorMessage: string
): Yup.Schema<unknown> => {
  if (typeof maxLength !== 'number') {
    return fieldSchema;
  }

  const maxNumber = parseInt('9'.repeat(maxLength), 10);
  return createOptionalNumberYupSchema(numericErrorMessage || 'Bitte geben sie eine gültige Zahl ein.').max(
    maxNumber,
    maxLengthErrorMessage
  );
};

/**
 * Applies required validation to a field schema based on conditions or isRequired flag
 *
 * @param fieldSchema - The current field schema
 * @param item - The form content item
 * @param form - The form
 * @param isRequired - Whether the field is required
 * @param requiredErrorMessage - The error message for required validation
 * @param formValues - The current form values
 * @returns The updated field schema
 */
export const applyRequiredValidation = (
  fieldSchema: Yup.Schema<unknown>,
  item: FormContentItem,
  form: FormStoryblok,
  isRequired: boolean,
  requiredErrorMessage: string,
  formValues: FormValues
): Yup.Schema<unknown> => {
  const conditions = getNestedConditions(item, form);
  const defaultErrorMessage =
    item.component === 'FormCheckbox' ? 'Bitte wählen Sie mindestens eine Option aus.' : 'Dieses Feld ist Pflicht.';
  const errorMessage = requiredErrorMessage || defaultErrorMessage;

  const applyRequiredConstraint = (schema: Yup.Schema<unknown>): Yup.Schema<unknown> => {
    if (item.component === 'FormCheckbox') {
      // A required checkbox group needs at least one selected option.
      // @ts-expect-error Yup.Schema does not expose the array-specific min method.
      return schema.min(1, errorMessage);
    }

    if (isFormMarketingOptIn(item)) {
      // @ts-expect-error Yup.Schema does not expose the array-specific min method.
      return schema.min(1, errorMessage);
    }

    return schema.required(errorMessage);
  };

  if (conditions.length) {
    const sources = conditions.map((c) => c.source);

    return fieldSchema.when(sources, {
      // Check if all conditions want the field to be required
      is: (...values: any[]) => {
        return conditions.every((cond, idx) => {
          const val = values[idx];
          // Determine if the condition matches
          const matches = (() => {
            switch (cond.condition) {
              case 'equals':
                return compareValueOrArray(val, cond.value);
              case 'notEquals':
                return !compareValueOrArray(val, cond.value);
              case 'isEmpty':
                return isEmptyValue(val);
              case 'notEmpty':
                return !isEmptyValue(val);
              default:
                return false;
            }
          })();

          // Determine if the field should be required based on the condition
          let wantsRequired: boolean;
          if (matches) {
            switch (cond.targetAction) {
              case 'require':
                wantsRequired = true;
                break;
              case 'show':
                wantsRequired = isRequired;
                break;
              case 'optional':
              case 'hide':
                wantsRequired = false;
                break;
              default:
                wantsRequired = isRequired;
                break;
            }
          } else {
            switch (cond.targetAction) {
              case 'optional':
              case 'hide':
                wantsRequired = isRequired;
                break;
              case 'show':
                wantsRequired = false;
                break;
              case 'require':
                wantsRequired = false;
                break;
              default:
                wantsRequired = isRequired;
                break;
            }
          }
          return wantsRequired;
        });
      },
      // If all conditions want the field to be required, make it required
      then: applyRequiredConstraint,
      // Otherwise, make it not required
      otherwise: (schema) => schema.notRequired(),
    });
  } else if (isRequired) {
    // If there are no conditions but the field is required
    return applyRequiredConstraint(fieldSchema);
  }

  return fieldSchema;
};

/**
 * Applies a validator to a field schema
 *
 * @param validator - The validator to apply
 * @param fieldSchema - The current field schema
 * @param formValues - The current form values
 * @param services - The services object
 * @param numericErrorMessage - The error message for numeric validation
 * @returns The updated field schema
 */
export const applyValidator = (
  validator: Validator,
  fieldSchema: Yup.Schema<unknown>,
  formValues: FormValues,
  services: Services,
  numericErrorMessage: string
): Yup.Schema<unknown> => {
  // Only apply validation if conditions are met
  if (!shouldApplyValidation(validator, formValues)) {
    return fieldSchema;
  }

  switch (validator.component) {
    /**
     * Numeric
     */
    case 'Numeric':
      return createOptionalNumberYupSchema(numericErrorMessage);

    /**
     * E-Mail validation
     */
    case 'Email':
      return createEmailValidator(validator.errorMessage);

    /**
     * Date
     */
    case 'Date':
      return createOptionalDateYupSchema(validator.errorMessage);

    /**
     * Phone
     */
    case 'Phone':
      const phoneRegex = /^\s*(?:\+?(\d{1,3}))?([-. (]*(\d{3})[-. )]*)?((\d{3})[-. ]*(\d{2,4})(?:[-.x ]*(\d+))?)\s*$/;
      return createRegexMatchSchema(fieldSchema, phoneRegex, validator.errorMessage);

    /**
     * Min Date
     */
    case 'MinDate':
      if (validator.timeSpan && validator.timeUnit) {
        const minDate = calculateDateFromNow(validator.timeSpan, validator.timeUnit, 'start');
        const errorMessageWithDate = replaceDateInErrorMessage(
          validator.errorMessage,
          validator.timeSpan,
          validator.timeUnit
        );
        return createDateConstraintSchema(fieldSchema, minDate, errorMessageWithDate, 'min');
      }
      return fieldSchema;

    /**
     * Max Date
     */
    case 'MaxDate':
      if (validator.timeSpan && validator.timeUnit) {
        const maxDate = calculateDateFromNow(validator.timeSpan, validator.timeUnit, 'end');
        const errorMessageWithDate = replaceDateInErrorMessage(
          validator.errorMessage,
          validator.timeSpan,
          validator.timeUnit
        );
        return createDateConstraintSchema(fieldSchema, maxDate, errorMessageWithDate, 'max');
      }
      return fieldSchema;

    /**
     * Age validation
     */
    case 'MinDateOfBirth':
      if (validator.years) {
        const currentDate = new Date();
        const minAge = parseInt(validator.years);
        const minDateOfBirth = new Date(currentDate.setFullYear(currentDate.getFullYear() - minAge));
        const errorMessageWithDate = replaceDateInErrorMessage(validator.errorMessage, `years`, validator.years);

        let updatedSchema = createDateConstraintSchema(fieldSchema, minDateOfBirth, errorMessageWithDate, 'max');

        // Add type error message
        if (Yup.isSchema(updatedSchema) && updatedSchema.type === 'date') {
          updatedSchema = (updatedSchema as Yup.DateSchema).typeError('Bitte geben Sie ein gültiges Datum an.');
        }
        return updatedSchema;
      }
      return fieldSchema;

    /**
     * Max characters
     */
    case 'MaxLength':
      if (typeof validator.maxLength === 'string') {
        const maxLength = parseInt(validator.maxLength, 10);
        const errorMessageWithLength = replaceLengthInErrorMessage(validator.errorMessage, maxLength);
        return createStringLengthSchema(fieldSchema, maxLength, errorMessageWithLength, 'max');
      }
      return fieldSchema;

    /**
     * Min characters
     */
    case 'MinLength':
      if (typeof validator.minLength === 'string') {
        const minLength = parseInt(validator.minLength, 10);
        const errorMessageWithLength = replaceLengthInErrorMessage(validator.errorMessage, minLength);
        return createStringLengthSchema(fieldSchema, minLength, errorMessageWithLength, 'min');
      }
      return fieldSchema;

    /**
     * Post Code validation
     */
    case 'PostCode':
      // Apply min length constraint
      let updatedSchema = createStringLengthSchema(fieldSchema, 4, validator.errorMessage, 'min');

      // Apply max length constraint
      updatedSchema = createStringLengthSchema(updatedSchema, 5, validator.errorMessage, 'max');

      // Apply regex pattern matching for digits only
      return createRegexMatchSchema(updatedSchema, /^\d+$/, validator.errorMessage);

    /**
     * BIC
     */
    case 'Bic':
      const bicRegex = /^([a-zA-Z]{4}[a-zA-Z]{2}[a-zA-Z0-9]{2}([a-zA-Z0-9]{3})?)$/;
      return createRegexMatchSchema(fieldSchema, bicRegex, validator.errorMessage);

    /**
     * IBAN
     */
    case 'Iban':
      return Yup.string().test('iban', validator.errorMessage, async (value) => {
        if (isSkippableEmptyFormValue(value)) {
          return true;
        }
        return await services.bankService.validate(value);
      });

    /**
     * Regex
     */
    case 'Regex':
      if ((validator as RegexStoryblok).regex) {
        return createRegexMatchSchema(fieldSchema, (validator as RegexStoryblok).regex!, validator.errorMessage);
      }
      return fieldSchema;

    default:
      return fieldSchema;
  }
};

// Helper functions from conditionsHelper.tsx that are needed for applyRequiredValidation
export const compareValueOrArray = (value: any, compareValue: any): boolean => {
  if (Array.isArray(value)) {
    return value.includes(compareValue);
  }
  return value === compareValue;
};

export const isEmptyValue = (value: any): boolean => {
  if (value === undefined || value === null || value === '') {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
};
